import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, 
  Package, 
  Globe, 
  Users, 
  ClipboardList, 
  Truck, 
  Factory,
  CheckCircle,
  FileText,
  Search,
  Handshake,
  BarChart3
} from 'lucide-react';

const ServicesPage = () => {
  const services = [
    {
      icon: Package,
      title: 'Procurement Outsourcing',
      description: 'Complete management of your purchasing process from sourcing to delivery. We handle the complexity so you can focus on your core business.',
      features: [
        'Full procurement cycle management',
        'Vendor relationship management',
        'Cost optimization strategies',
        'Compliance and risk management',
      ],
    },
    {
      icon: Globe,
      title: 'International Sourcing',
      description: 'Access our global supplier network with competitive pricing and quality assurance. We identify the best sources for your specific needs.',
      features: [
        'Global supplier database access',
        'Competitive pricing negotiations',
        'Quality verification processes',
        'Multi-country sourcing capabilities',
      ],
      featured: true,
    },
    {
      icon: Users,
      title: 'Supplier Identification & Negotiation',
      description: 'Expert vetting and selection of reliable suppliers worldwide. We negotiate favorable terms on your behalf.',
      features: [
        'Thorough supplier vetting',
        'Financial stability assessment',
        'Contract negotiation support',
        'Supplier performance tracking',
      ],
    },
    {
      icon: ClipboardList,
      title: 'Order Management',
      description: 'Streamlined order processing and tracking systems that keep you informed at every stage.',
      features: [
        'Purchase order processing',
        'Real-time tracking systems',
        'Inventory management support',
        'Delivery scheduling',
      ],
    },
    {
      icon: Truck,
      title: 'Logistics Coordination',
      description: 'End-to-end logistics management and customs clearance. We ensure your goods arrive safely and on time.',
      features: [
        'Freight forwarding coordination',
        'Customs clearance assistance',
        'Documentation management',
        'Last-mile delivery solutions',
      ],
    },
    {
      icon: Factory,
      title: 'Industrial & Agro-Industrial Supply',
      description: 'Specialized sourcing for industrial equipment, machinery, and agricultural inputs with technical expertise.',
      features: [
        'Technical specifications review',
        'Equipment sourcing',
        'Spare parts procurement',
        'Agricultural input supply',
      ],
    },
  ];

  const process = [
    {
      step: '01',
      icon: FileText,
      title: 'Requirements Analysis',
      description: 'We begin by understanding your specific needs, budget, and timeline.',
    },
    {
      step: '02',
      icon: Search,
      title: 'Supplier Sourcing',
      description: 'Our team identifies and evaluates potential suppliers from our global network.',
    },
    {
      step: '03',
      icon: Handshake,
      title: 'Negotiation & Contracting',
      description: 'We negotiate the best terms and manage all contractual requirements.',
    },
    {
      step: '04',
      icon: BarChart3,
      title: 'Execution & Monitoring',
      description: 'We oversee the entire process and provide regular progress updates.',
    },
  ];

  return (
    <div data-testid="services-page">
      {/* Hero Section */}
      <section className="relative bg-[#0F172A] pt-32 pb-20 noise-overlay">
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="text-[#0EA5E9] text-sm font-medium uppercase tracking-widest">
              Our Services
            </span>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6">
              Comprehensive Procurement Solutions
            </h1>
            <p className="text-slate-400 text-lg leading-relaxed">
              From sourcing to delivery, we provide end-to-end procurement services 
              tailored to your business needs.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section data-testid="services-grid" className="bg-[#F8FAFC] py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={service.title}
                data-testid={`service-detail-${index}`}
                className={`group bg-white border border-slate-200 hover:border-[#0EA5E9] hover:shadow-xl transition-all duration-300 ${
                  service.featured ? 'lg:col-span-2' : ''
                }`}
              >
                <div className="p-8">
                  <div className={`w-14 h-14 flex items-center justify-center mb-6 ${
                    service.featured ? 'bg-[#0EA5E9]' : 'bg-[#0F172A]'
                  }`}>
                    <service.icon className="text-white" size={28} />
                  </div>
                  <h3 className="font-['Outfit'] text-xl md:text-2xl font-semibold text-[#0F172A] mb-4 group-hover:text-[#0EA5E9] transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed mb-6">
                    {service.description}
                  </p>
                  <ul className="space-y-3">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle className="text-[#0EA5E9] mt-0.5 flex-shrink-0" size={18} />
                        <span className="text-slate-600 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="border-t border-slate-200 p-6 bg-[#F8FAFC]">
                  <Link
                    to="/contact"
                    className="inline-flex items-center text-[#0EA5E9] font-medium group/link"
                  >
                    Request this service
                    <ArrowRight className="ml-2 group-hover/link:translate-x-1 transition-transform" size={16} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section data-testid="process-section" className="bg-white py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-[#0EA5E9] text-sm font-medium uppercase tracking-widest">
              How We Work
            </span>
            <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold text-[#0F172A] mt-4 mb-4">
              Our Process
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              A structured approach that ensures transparency and results
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <div
                key={step.title}
                data-testid={`process-step-${index}`}
                className="relative"
              >
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-12 left-full w-full h-px bg-slate-200 -translate-x-8"></div>
                )}
                <div className="text-center">
                  <div className="relative inline-block mb-6">
                    <div className="w-24 h-24 bg-[#F8FAFC] border border-slate-200 flex items-center justify-center">
                      <step.icon className="text-[#0F172A]" size={32} />
                    </div>
                    <span className="absolute -top-2 -right-2 w-8 h-8 bg-[#0EA5E9] text-white text-sm font-bold flex items-center justify-center">
                      {step.step}
                    </span>
                  </div>
                  <h3 className="font-['Outfit'] text-lg font-semibold text-[#0F172A] mb-2">
                    {step.title}
                  </h3>
                  <p className="text-slate-600 text-sm">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="bg-[#0F172A] py-20 md:py-32 noise-overlay">
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#0EA5E9] text-sm font-medium uppercase tracking-widest">
                Our Commitment
              </span>
              <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold text-white mt-4 mb-6">
                Delivering Value at Every Step
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                We understand that procurement is not just about buying — it's about 
                creating value. Our services are designed to reduce costs, minimize 
                risks, and optimize your supply chain efficiency.
              </p>
              <div className="grid sm:grid-cols-2 gap-6">
                {[
                  { value: '20%+', label: 'Average Cost Savings' },
                  { value: '50%', label: 'Time Reduction' },
                  { value: '99%', label: 'On-Time Delivery' },
                  { value: '24/7', label: 'Support Available' },
                ].map((stat, index) => (
                  <div key={stat.label} className="bg-[#1E293B] p-6 border border-slate-700">
                    <div className="font-['Outfit'] text-3xl font-bold text-[#0EA5E9] mb-1">
                      {stat.value}
                    </div>
                    <div className="text-slate-400 text-sm">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 border border-[#0EA5E9]/20"></div>
              <img
                src="/7.jpeg"
                alt="Global Logistics Network"
                className="w-full h-[450px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#0EA5E9] py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-['Outfit'] text-2xl md:text-3xl font-semibold text-white mb-4">
            Need a Custom Procurement Solution?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-xl mx-auto">
            Contact us to discuss your specific requirements and receive a tailored proposal.
          </p>
          <Link to="/contact">
            <Button
              data-testid="services-cta-btn"
              className="bg-white text-[#0EA5E9] hover:bg-[#F8FAFC] rounded-none px-8 py-4 font-medium transition-colors duration-300"
            >
              Request a Quote
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
